import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type DemoSystem, type CatalogPattern } from '../lib/api';
import { DemoPanel } from '../components/DemoPanel';
import { PatternDiagram } from '../components/PatternDiagram';

const TRANSITIONAL = new Set(['pending', 'stopping', 'shutting-down', 'rebooting']);

export function Dashboard() {
  const [systems, setSystems] = useState<DemoSystem[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogPattern[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [awaiting, setAwaiting] = useState<string | null>(null); // stack name we're waiting to appear
  const [refreshing, setRefreshing] = useState(false); // manual refresh in flight (button feedback)
  const [submitting, setSubmitting] = useState(false); // deploy request in flight (double-submit guard)
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const [sys, cat] = await Promise.all([
        api.systems(),
        api.catalog().catch(() => ({ patterns: [] as CatalogPattern[] })),
      ]);
      setSystems(sys.systems);
      setCatalog(cat.patterns);
      setError(null);
      // Stop the deploy watch once the new system shows up — matched by stack name (cloud) or id
      // (local podman is synchronous; ml-lab is async and appears in the list a few seconds later).
      setAwaiting((prev) => (prev && sys.systems.some((s) => s.stackName === prev || s.id === prev) ? null : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load');
    }
  }, []);

  // Manual refresh with visible feedback (the button shows "Refreshing…" and disables while in flight).
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const transitioning = systems?.some((s) => TRANSITIONAL.has(s.state));
    if (transitioning || awaiting) {
      timer.current = window.setTimeout(() => void load(), 5000);
      return () => window.clearTimeout(timer.current);
    }
  }, [systems, awaiting, load]);

  const act = useCallback(
    async (id: string, op: 'start' | 'stop') => {
      setBusy((b) => ({ ...b, [id]: true }));
      try {
        await (op === 'start' ? api.startSystem(id) : api.stopSystem(id));
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : `failed to ${op}`);
      } finally {
        setBusy((b) => ({ ...b, [id]: false }));
      }
    },
    [load],
  );

  const deploy = useCallback(
    async (
      patternId: string,
      opts?: {
        name?: string;
        allowedCidr?: string;
        expose?: boolean;
        sso?: boolean;
        exposeGrafana?: boolean;
        nodeCount?: number;
        drivesPerNode?: number;
        capacityGiB?: number;
      },
    ) => {
      if (submitting) return; // guard against a double-click before the request settles
      setError(null);
      setSubmitting(true);
      try {
        const res = await api.deploySystem(patternId, opts);
        // Watch until the system appears: cloud by stackName, ml-lab (async VM boot) by id. Local
        // podman is synchronous so it's already present on the reload below (awaiting clears at once).
        setAwaiting(res.stackName ?? res.id ?? null);
        await load();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'failed to deploy';
        setError(
          msg === 'cap_reached'
            ? 'Capacity reached — destroy an existing system first.'
            : msg === 'resource_cap'
              ? 'Too many local containers — destroy a system or choose fewer nodes.'
            : msg === 'no_slots'
              ? 'No public hostnames free — destroy an exposed system to free a slot.'
            : msg === 'slot_taken'
              ? 'That deployment name is already exposed — pick a different name.'
            : msg === 'deploy_not_enabled'
              ? 'Self-service deploy is not enabled yet.'
              : msg === 'sso_not_configured'
                ? 'Cloud SSO isn’t configured yet (needs the Cloudflare API token + account/zone).'
                : msg === 'need_ipv4'
                ? 'Couldn’t detect your IPv4 address — enter the allowed source IP (e.g. 203.0.113.4/32).'
                : msg === 'invalid_cidr'
                  ? 'Enter a valid IPv4 CIDR, e.g. 203.0.113.4/32.'
                  : msg,
        );
      } finally {
        setSubmitting(false);
      }
    },
    [load, submitting],
  );

  const destroy = useCallback(
    async (id: string) => {
      setBusy((b) => ({ ...b, [id]: true }));
      try {
        await api.destroySystem(id);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'failed to destroy');
      } finally {
        setBusy((b) => ({ ...b, [id]: false }));
      }
    },
    [load],
  );

  return (
    <main className="content">
      <div className="row-between">
        <h1>Demo systems</h1>
        <button type="button" className="btn ghost" disabled={refreshing} onClick={() => void refresh()}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {catalog.length > 0 && (
        <LaunchPanel patterns={catalog} awaiting={awaiting} submitting={submitting} onDeploy={deploy} />
      )}

      {systems === null && !error && <div className="placeholder loading">Loading demo systems…</div>}
      {/* First-run orientation: the four-beat flow, shown only while there's nothing else to see. */}
      {systems?.length === 0 && !awaiting && catalog.length > 0 && (
        <div className="howto">
          <div className="howto-title">No demo systems yet — here’s the flow</div>
          <ol className="howto-steps">
            <li>
              <b>Launch</b>
              <span>pick a pattern above and deploy — local patterns are ~$0 and start in seconds</span>
            </li>
            <li>
              <b>Access</b>
              <span>open the Console, or mint scoped S3 keys, straight from the system card</span>
            </li>
            <li>
              <b>Attach a demo</b>
              <span>pick the capability story — Bakehouse → Databricks, the AIStor capabilities tour…</span>
            </li>
            <li>
              <b>Present</b>
              <span>run the walkthrough in its own tab — narrated steps, live operations, present mode</span>
            </li>
          </ol>
        </div>
      )}
      {systems?.length === 0 && !awaiting && catalog.length === 0 && (
        <div className="placeholder">No demo systems yet.</div>
      )}
      {awaiting && systems?.length === 0 && <div className="placeholder loading">Deploying — waiting for the system to appear…</div>}

      <div className="systems">
        {systems?.map((s) => (
          <SystemCard
            key={s.id}
            sys={s}
            busy={!!busy[s.id]}
            deletable={s.deletable}
            patternLabel={catalog.find((c) => c.id === s.pattern)?.label ?? s.pattern}
            onAct={act}
            onDestroy={destroy}
          />
        ))}
      </div>
    </main>
  );
}

function LaunchPanel({
  patterns,
  awaiting,
  submitting,
  onDeploy,
}: {
  patterns: CatalogPattern[];
  awaiting: string | null;
  submitting: boolean;
  onDeploy: (
    patternId: string,
    opts?: {
      name?: string;
      allowedCidr?: string;
      expose?: boolean;
      sso?: boolean;
      exposeGrafana?: boolean;
      nodeCount?: number;
      drivesPerNode?: number;
      capacityGiB?: number;
    },
  ) => void;
}) {
  // Deep-linkable pattern preselect (?pattern=<id>) — the Demos page routes here with the
  // recommended pattern for a demo already chosen.
  const [patternId, setPatternId] = useState(() => {
    const want = new URLSearchParams(window.location.search).get('pattern');
    return want && patterns.some((x) => x.id === want) ? want : (patterns[0]?.id ?? '');
  });
  const [name, setName] = useState('');
  // The demo node's security group is locked to this IPv4 CIDR. Prefill with the browser's public
  // IPv4 (the node is IPv4-only, and the dashboard may be reached over IPv6 so the server can't
  // infer it). Editable in case the lookup is wrong or you want a wider range.
  const [cidr, setCidr] = useState('');
  // Local (podman) only: how the Console is reached — private (localhost), a public HTTPS URL via the
  // Cloudflare tunnel, or public + seamless SSO into the Console (dashboard OIDC provider).
  const [exposure, setExposure] = useState<'private' | 'public' | 'sso'>('private');
  // Capabilities-lab only: also publish Grafana over the tunnel. Off by default — Grafana stays a
  // localhost link unless this is on (and only takes effect when the lab itself is exposed).
  const [exposeGrafana, setExposeGrafana] = useState(false);
  // Cloud (aws-cfn) only: expose the Console via a per-instance tunnel + SSO (no public IP / CIDR).
  const [cloudSso, setCloudSso] = useState(false);
  // Custom local cluster shape (only shown for shapeKind:'custom' patterns).
  const [nodeCount, setNodeCount] = useState(1);
  const [drives, setDrives] = useState(1);
  const [capacity, setCapacity] = useState(30);
  const p = patterns.find((x) => x.id === patternId) ?? patterns[0];
  const custom = p?.shapeKind === 'custom' ? p.custom : undefined;
  const totalDrives = nodeCount * drives;
  const atCap = p ? p.remaining <= 0 : true;

  // Seed the shape inputs from the selected custom pattern's defaults when the pattern changes.
  useEffect(() => {
    const sel = patterns.find((x) => x.id === patternId);
    if (sel?.shapeKind === 'custom' && sel.custom) {
      setNodeCount(sel.custom.defaultNodes);
      setDrives(sel.custom.defaultDrivesPerNode);
      setCapacity(sel.custom.defaultCapacityGiB);
    }
  }, [patternId, patterns]);
  // Greyed while the request is in flight (both backends) AND while a cloud stack is still coming up.
  const deploying = submitting || !!awaiting;
  // Backend-driven field visibility (parity): CIDR for cloud, exposure choice for local.
  const isCloud = !!p?.backends?.includes('aws-cfn');
  const isLocal = !!p?.backends?.includes('local-podman');

  useEffect(() => {
    let cancelled = false;
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((d: { ip?: string }) => {
        if (!cancelled && d.ip && d.ip.includes('.')) setCidr(`${d.ip}/32`);
      })
      .catch(() => {
        /* lookup failed — the field stays empty for manual entry */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="launch">
      <div className="launch-head">
        <h2>Launch demo system</h2>
        {p && (
          <span className={`pill ${atCap ? 'off' : 'ok'}`}>
            {p.activeCount}/{p.concurrencyCap} used
          </span>
        )}
      </div>

      {/* Pattern gallery: one selectable tile per catalog pattern, with its architecture thumbnail
          and the facts that drive the choice (license, backend, shape, cost). The full-size diagram
          + description for the selected pattern still render below. */}
      <div className="pattern-tiles" role="radiogroup" aria-label="Deploy pattern">
        {patterns.map((x) => (
          <button
            key={x.id}
            type="button"
            role="radio"
            aria-checked={x.id === patternId}
            className={`tile ${x.id === patternId ? 'on' : ''}`}
            onClick={() => setPatternId(x.id)}
          >
            <span className="tile-thumb" aria-hidden="true">
              <PatternDiagram
                patternId={x.id}
                nodeCount={x.custom?.defaultNodes}
                drivesPerNode={x.custom?.defaultDrivesPerNode}
              />
            </span>
            <span className="tile-label">{x.label}</span>
            <span className="tile-tags">
              <span className="tier">{x.licenseTier}</span>
              <span className="tile-tag">{x.backends.includes('aws-cfn') ? 'cloud' : 'local'}</span>
              <span className="tile-tag">
                {x.shapeKind === 'custom' ? 'choose shape' : `${x.nodeCount} node${x.nodeCount === 1 ? '' : 's'}`}
              </span>
              <span className="tile-tag">~${x.estCostMonthlyRunning}/mo</span>
              {x.remaining <= 0 && <span className="tile-tag cap">at capacity</span>}
            </span>
          </button>
        ))}
      </div>

      <div className="launch-row">
        <label className="field">
          <span>Name (optional)</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. databricks-demo"
            maxLength={20}
          />
        </label>
        {isCloud && (
          <label className="field checkbox">
            <span>Access</span>
            <label className="check-inline">
              <input type="checkbox" checked={cloudSso} onChange={(e) => setCloudSso(e.target.checked)} />
              <span className="muted small-note">Seamless SSO via Cloudflare tunnel (no public IP / CIDR)</span>
            </label>
          </label>
        )}
        {isCloud && !cloudSso && (
          <label className="field">
            <span>Allowed source IP (IPv4 CIDR)</span>
            <input
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              placeholder="203.0.113.4/32"
            />
          </label>
        )}
        {isLocal && (
          <label className="field">
            <span>Exposure</span>
            <select value={exposure} onChange={(e) => setExposure(e.target.value as typeof exposure)}>
              <option value="private">Private (localhost)</option>
              <option value="public">Public URL (Cloudflare tunnel)</option>
              <option value="sso">Public URL + SSO</option>
            </select>
          </label>
        )}
        {isLocal && exposure !== 'private' && (
          <label className="field checkbox">
            <span>Expose Grafana</span>
            <label className="check-inline">
              <input type="checkbox" checked={exposeGrafana} onChange={(e) => setExposeGrafana(e.target.checked)} />
              <span className="muted small-note">
                Publish Grafana on a public URL too (SSO if enabled). Off = localhost-only.
              </span>
            </label>
          </label>
        )}
      </div>

      {custom && (
        <div className="launch-row">
          {/* Node count is only offered when the pattern permits >1 (multi-node distributed is a
              licensed AIStor feature; on Free it is capped at 1, so the input is hidden). */}
          {custom.maxNodes > 1 && (
            <label className="field">
              <span>Nodes (containers)</span>
              <input
                type="number"
                min={custom.minNodes}
                max={custom.maxNodes}
                value={nodeCount}
                onChange={(e) => setNodeCount(Number(e.target.value))}
              />
            </label>
          )}
          <label className="field">
            <span>Drives{custom.maxNodes > 1 ? ' / node' : ''}</span>
            <input
              type="number"
              min={custom.minDrivesPerNode}
              max={custom.maxDrivesPerNode}
              value={drives}
              onChange={(e) => setDrives(Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span>Capacity / drive (GiB)</span>
            <input
              type="number"
              min={1}
              max={custom.maxCapacityGiB}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
            />
          </label>
          <div className="field">
            <span>Shape</span>
            <span className="muted small-note">
              {custom.maxNodes > 1 ? `${nodeCount} × ${drives} = ` : ''}
              <strong>{totalDrives}</strong> drive{totalDrives === 1 ? '' : 's'} ·{' '}
              {totalDrives >= 4
                ? 'erasure coding active'
                : totalDrives > 1
                  ? 'multi-drive (no parity until 4 drives)'
                  : 'standalone (single drive)'}{' '}
              · capacity is advisory
            </span>
          </div>
        </div>
      )}

      {p && (
        <>
          <p className="pattern-desc">{p.description}</p>
          <PatternDiagram patternId={p.id} nodeCount={nodeCount} drivesPerNode={drives} />
          <dl className="system-meta">
            <div>
              <dt>Nodes</dt>
              <dd>{p.nodeCount}</dd>
            </div>
            <div>
              <dt>License</dt>
              <dd>
                <span className="tier">{p.licenseTier}</span>
              </dd>
            </div>
            <div>
              <dt>Instance</dt>
              <dd>
                {p.instanceType} · {p.arch}
              </dd>
            </div>
            <div>
              <dt>Est. cost</dt>
              <dd>
                ~${p.estCostMonthlyRunning}/mo running · ~${p.estCostMonthlyStopped}/mo stopped
              </dd>
            </div>
            <div>
              <dt>Auto-destroy</dt>
              <dd>{p.ttlHours}h</dd>
            </div>
          </dl>
        </>
      )}

      <div className="actions">
        <button
          type="button"
          className="btn primary"
          disabled={!p || atCap || deploying}
          onClick={() =>
            onDeploy(patternId, {
              name: name.trim() || undefined,
              allowedCidr: isCloud && !cloudSso ? cidr.trim() || undefined : undefined,
              expose: isLocal ? exposure !== 'private' : undefined,
              sso: isLocal ? exposure === 'sso' : isCloud ? cloudSso : undefined,
              exposeGrafana: isLocal ? exposure !== 'private' && exposeGrafana : undefined,
              nodeCount: custom ? nodeCount : undefined,
              drivesPerNode: custom ? drives : undefined,
              capacityGiB: custom ? capacity : undefined,
            })
          }
        >
          {deploying ? 'Deploying…' : atCap ? 'At capacity' : 'Deploy'}
        </button>
        {atCap && !deploying && (
          <span className="muted small-note">Destroy an existing system to free capacity.</span>
        )}
      </div>
    </section>
  );
}

function CopyField({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="copyfield">
      <span className="copyfield-label">{label}</span>
      <code className={secret ? 'secret' : undefined}>{value}</code>
      <button type="button" className="btn ghost small" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** A multi-line copy block (e.g. a set of shell commands) with a single copy-all button. */
function CopyBlock({ label, lines }: { label: string; lines: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = lines.join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="copyblock">
      <div className="copyblock-head">
        <span className="copyfield-label">{label}</span>
        <button type="button" className="btn ghost small" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy all'}
        </button>
      </div>
      <pre>{text}</pre>
    </div>
  );
}

interface AdminLogin {
  username: string;
  password: string;
  consoleUrl: string | null;
  grafana?: { username: string; password: string; url: string | null };
}
interface GeneratedKey {
  accessKey: string;
  secretKey: string;
}

function SystemCard({
  sys,
  busy,
  deletable,
  patternLabel,
  onAct,
  onDestroy,
}: {
  sys: DemoSystem;
  busy: boolean;
  deletable: boolean;
  patternLabel: string | null;
  onAct: (id: string, op: 'start' | 'stop') => void;
  onDestroy: (id: string) => void;
}) {
  const running = sys.state === 'running';
  const stopped = sys.state === 'stopped';
  const transitioning = TRANSITIONAL.has(sys.state);
  const disabled = busy || transitioning;
  // A capabilities lab is a multi-cluster system — it carries peer Console URLs. Its primary Console
  // button is named after the cluster (lab1) rather than a bare "Open Console".
  const isLab = !!sys.serviceUrls?.console2;
  // Interactive console sessions (shell) are local-podman only; the button is gated + tooltip'd on this.
  const isLocal = sys.backend === 'local-podman';

  const [admin, setAdmin] = useState<AdminLogin | null>(null);
  const [key, setKey] = useState<GeneratedKey | null>(null);
  const [pending, setPending] = useState<null | 'admin' | 'key'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showDemos, setShowDemos] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const showAdmin = async () => {
    if (admin) {
      setAdmin(null);
      return;
    }
    setPending('admin');
    setErr(null);
    try {
      setAdmin(await api.adminLogin(sys.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to fetch admin login');
    } finally {
      setPending(null);
    }
  };

  const genKey = async () => {
    setPending('key');
    setErr(null);
    try {
      setKey(await api.generateKey(sys.id));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'failed to generate key';
      setErr(
        msg === 'not_running'
          ? 'Start the system before generating a key.'
          : msg === 'not_supported'
            ? 'Key generation isn’t wired for this backend yet — use the admin login or the cluster’s own tools.'
            : msg,
      );
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="system">
      <div className="system-head">
        <div className="system-title">
          <span className="system-name">{sys.name}</span>
          {/* The pattern is the "what is this?" answer — it leads; plumbing (ids, types) lives
              behind Details below. */}
          {(patternLabel ?? sys.pattern) && <span className="system-pattern">{patternLabel ?? sys.pattern}</span>}
        </div>
        <span className={`pill ${running ? 'ok' : stopped ? 'off' : 'warn'}`}>{sys.state}</span>
      </div>
      <dl className="system-meta">
        <div>
          <dt>Nodes</dt>
          <dd>
            {sys.nodeCount}
            {sys.drivesPerNode != null && sys.drivesPerNode > 1
              ? ` × ${sys.drivesPerNode} drives`
              : ''}
          </dd>
        </div>
        <div>
          <dt>License</dt>
          <dd>
            <span className="tier">{sys.licenseTier}</span>
          </dd>
        </div>
        <div>
          <dt>{sys.oidcEnabled ? 'Access' : 'Public IP'}</dt>
          <dd>
            {sys.oidcEnabled ? (
              <span className="muted small-note">Tunnel · inbound closed</span>
            ) : (
              (sys.publicIp ?? '—')
            )}
          </dd>
        </div>
      </dl>

      <div className="actions">
        <button type="button" className="btn" onClick={() => setShowDemos((v) => !v)}>
          {showDemos ? 'Hide demos' : 'Demos'}
        </button>
        <a
          className={`btn ${sys.consoleUrl ? '' : 'disabled'}`}
          href={sys.consoleUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!sys.consoleUrl}
        >
          {isLab ? 'lab1 Console' : 'Open Console'}
        </a>
        {/* Capabilities-lab peer/tier/observability endpoints (only while running). Each cluster's
            Console is named after the cluster so it's clear which one the button opens. */}
        {running && sys.serviceUrls?.console2 && (
          <a className="btn ghost" href={sys.serviceUrls.console2} target="_blank" rel="noopener noreferrer">
            lab2 Console
          </a>
        )}
        {running && sys.serviceUrls?.console3 && (
          <a className="btn ghost" href={sys.serviceUrls.console3} target="_blank" rel="noopener noreferrer">
            tier Console
          </a>
        )}
        {running && sys.serviceUrls?.grafana && (
          <a className="btn ghost" href={sys.serviceUrls.grafana} target="_blank" rel="noopener noreferrer">
            Grafana
          </a>
        )}
        {running && sys.serviceUrls?.prometheus && (
          <a className="btn ghost" href={sys.serviceUrls.prometheus} target="_blank" rel="noopener noreferrer">
            Prometheus
          </a>
        )}
        {running ? (
          <button type="button" className="btn ghost" disabled={disabled} onClick={() => onAct(sys.id, 'stop')}>
            {busy ? 'Stopping…' : 'Stop'}
          </button>
        ) : (
          <button
            type="button"
            className="btn ghost"
            disabled={disabled || !stopped}
            onClick={() => onAct(sys.id, 'start')}
          >
            {busy ? 'Starting…' : 'Start'}
          </button>
        )}
      </div>

      <div className="actions">
        <button type="button" className="btn ghost" disabled={pending === 'admin'} onClick={() => void showAdmin()}>
          {pending === 'admin' ? 'Loading…' : admin ? 'Hide admin login' : 'Show admin login'}
        </button>
        <button
          type="button"
          className="btn ghost"
          disabled={pending === 'key' || !running}
          title={running ? '' : 'Start the system first'}
          onClick={() => void genKey()}
        >
          {pending === 'key' ? 'Generating…' : 'Generate S3 key'}
        </button>
        {/* Console session: an interactive shell inside the container, mc pre-wired. Local (podman)
            systems only — the daemon rejects other backends; cloud parity is a cost-gated follow-on. */}
        <button
          type="button"
          className="btn ghost"
          disabled={!isLocal || !running}
          title={
            !isLocal
              ? 'Console sessions are available for local (podman) systems — cloud parity is coming'
              : running
                ? 'Open an interactive shell inside the system (mc ready)'
                : 'Start the system first'
          }
          onClick={() => window.open(`/shell?system=${encodeURIComponent(sys.id)}`, '_blank', 'noopener')}
        >
          Shell
        </button>
        <button type="button" className="btn ghost" onClick={() => setShowDetails((v) => !v)}>
          {showDetails ? 'Hide details' : 'Details'}
        </button>
        {deletable && (
          <button
            type="button"
            className="btn ghost danger"
            disabled={disabled}
            onClick={() => {
              setConfirming((v) => !v);
              setConfirmText('');
            }}
          >
            {confirming ? 'Cancel' : busy ? 'Destroying…' : 'Destroy'}
          </button>
        )}
      </div>

      {err && <p className="error">{err}</p>}

      {showDetails && (
        <div className="reveal">
          <div className="reveal-title">System details</div>
          <dl className="system-meta">
            <div>
              <dt>Instance</dt>
              <dd>{sys.id}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>
                {sys.type}
                {sys.arch ? ` · ${sys.arch}` : ''}
              </dd>
            </div>
            {sys.pattern && (
              <div>
                <dt>Pattern id</dt>
                <dd>{sys.pattern}</dd>
              </div>
            )}
            {sys.drivesPerNode != null && sys.drivesPerNode > 1 && (
              <div>
                <dt>Drives</dt>
                <dd>
                  {sys.drivesPerNode}/node · {sys.nodeCount * sys.drivesPerNode} total
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {confirming && (
        <div className="reveal danger">
          <div className="reveal-title">Destroy {sys.name}?</div>
          <p className="muted small-note">
            This permanently tears down the system — the instance/container and its storage volume —
            and deletes its data. Type the name to confirm.
          </p>
          {sys.mcAliases && sys.mcAliases.length > 0 && (
            <CopyBlock
              label="Remove local mc aliases after destroy"
              lines={sys.mcAliases.map((a) => `mc alias rm ${a.name}`)}
            />
          )}
          <input
            className="confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={sys.name}
          />
          <button
            type="button"
            className="btn danger"
            disabled={confirmText !== sys.name || busy}
            onClick={() => onDestroy(sys.id)}
          >
            Confirm destroy
          </button>
        </div>
      )}

      {admin && (
        <div className="reveal">
          <div className="reveal-title">AIStor admin (Console login)</div>
          <CopyField label="Username" value={admin.username} />
          <CopyField label="Password" value={admin.password} secret />
          {admin.consoleUrl && (
            <a className="btn small" href={admin.consoleUrl} target="_blank" rel="noopener noreferrer">
              Open Console
            </a>
          )}
          {sys.mcAliases && sys.mcAliases.length > 0 && (
            <CopyBlock
              label="mc aliases (local client)"
              lines={sys.mcAliases.map(
                (a) => `mc alias set ${a.name} ${a.url} ${admin.username} ${admin.password}`,
              )}
            />
          )}
          {admin.grafana && (
            <>
              <div className="reveal-title" style={{ marginTop: '0.75rem' }}>
                Grafana admin (local fallback; SSO if enabled)
              </div>
              <CopyField label="Username" value={admin.grafana.username} />
              <CopyField label="Password" value={admin.grafana.password} secret />
              {admin.grafana.url && (
                <a className="btn small" href={admin.grafana.url} target="_blank" rel="noopener noreferrer">
                  Open Grafana
                </a>
              )}
            </>
          )}
        </div>
      )}

      {key && (
        <div className="reveal">
          <div className="reveal-title">New S3 service account — full access</div>
          <CopyField label="Access key" value={key.accessKey} />
          <CopyField label="Secret key" value={key.secretKey} secret />
          <p className="muted small-note">
            Copy the secret now — it isn’t shown again. Revoke with{' '}
            <code>mc admin user svcacct rm</code>.
          </p>
        </div>
      )}

      {showDemos && <DemoPanel systemId={sys.id} running={running} />}
    </div>
  );
}
